import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { BarChart3 } from 'lucide-react'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid } from 'recharts'
import { api, formatMoney } from '@/lib/api'

const COR_CLASSE: Record<string, string> = { A: '#16a34a', B: '#d97706', C: '#dc2626' }

export default function CurvaABCPage() {
  const [por, setPor] = useState<'produto' | 'cliente'>('produto')
  const [de, setDe] = useState('')
  const [ate, setAte] = useState('')

  const { data, isLoading } = useQuery<any>({
    queryKey: ['curva-abc', por, de, ate],
    queryFn: async () => (await api.get('/comercial/relatorios/curva-abc', { params: { por, de: de || undefined, ate: ate || undefined } })).data,
  })

  const itens = data?.itens || []
  const resumo = data?.resumo || { A: { qtd: 0, valor: 0 }, B: { qtd: 0, valor: 0 }, C: { qtd: 0, valor: 0 } }
  const total = data?.total || 1
  const top = itens.slice(0, 15).map((i: any) => ({ nome: (i.nome || '').slice(0, 22), valor: i.valor, classe: i.classe }))

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-xl font-bold flex items-center gap-2"><BarChart3 size={22} /> Curva ABC</h1>
        <div className="flex items-center gap-2 text-sm">
          <div className="flex rounded-lg overflow-hidden border">
            {(['produto', 'cliente'] as const).map(p => (
              <button key={p} onClick={() => setPor(p)} className={`px-3 py-1.5 ${por === p ? 'bg-brand-600 text-white' : 'bg-white text-ink-600'}`}>{p === 'produto' ? 'Por produto' : 'Por cliente'}</button>
            ))}
          </div>
          <input type="date" value={de} onChange={e => setDe(e.target.value)} className="input !py-1" />
          <span className="text-ink-400">até</span>
          <input type="date" value={ate} onChange={e => setAte(e.target.value)} className="input !py-1" />
        </div>
      </div>

      {/* Resumo A/B/C */}
      <div className="grid grid-cols-3 gap-3">
        {(['A', 'B', 'C'] as const).map(c => (
          <div key={c} className="card card-body">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-3 h-3 rounded-full" style={{ background: COR_CLASSE[c] }} />
              <span className="text-xs uppercase text-ink-500">Classe {c}</span>
            </div>
            <div className="text-xl font-bold">{formatMoney(resumo[c].valor)}</div>
            <div className="text-xs text-ink-500">{resumo[c].qtd} item(ns) · {((resumo[c].valor / total) * 100).toFixed(1)}%</div>
          </div>
        ))}
      </div>

      {/* Gráfico top 15 */}
      <div className="card card-body">
        <div className="text-xs font-semibold text-ink-500 uppercase mb-2">Top 15 — {por}</div>
        <div style={{ width: '100%', height: 320 }}>
          <ResponsiveContainer>
            <BarChart data={top} margin={{ left: 10, right: 10, top: 5, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="nome" angle={-35} textAnchor="end" interval={0} tick={{ fontSize: 10 }} height={70} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v: any) => formatMoney(Number(v))} />
              <Bar dataKey="valor" radius={[3, 3, 0, 0]}>
                {top.map((t: any, i: number) => <Cell key={i} fill={COR_CLASSE[t.classe] || '#6366f1'} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Tabela */}
      <div className="table-wrap max-h-[60vh] overflow-auto">
        <table className="table text-sm">
          <thead className="sticky top-0 bg-white"><tr><th>#</th><th>{por === 'produto' ? 'Produto' : 'Cliente'}</th><th className="text-right">Valor</th><th className="text-right">%</th><th className="text-right">% acum.</th><th>Classe</th></tr></thead>
          <tbody>
            {isLoading && <tr><td colSpan={6} className="text-center py-4 text-ink-500">Carregando…</td></tr>}
            {itens.map((i: any, idx: number) => (
              <tr key={i.id}>
                <td className="text-ink-400">{idx + 1}</td>
                <td>{i.nome}</td>
                <td className="text-right font-mono">{formatMoney(i.valor)}</td>
                <td className="text-right">{i.pct}%</td>
                <td className="text-right text-ink-500">{i.pct_acum}%</td>
                <td><span className="px-2 py-0.5 rounded-full text-xs font-bold text-white" style={{ background: COR_CLASSE[i.classe] }}>{i.classe}</span></td>
              </tr>
            ))}
            {!isLoading && itens.length === 0 && <tr><td colSpan={6} className="text-center py-4 text-ink-500">Sem vendas no período.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  )
}
